import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { WatchQueryRef } from '@akebono/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, startWith, switchMap, takeUntil } from 'rxjs/operators';
import {
  LotsListQuery,
  LotsListQueryVariables,
} from 'src/app/modules/graphql/service/graphql-cars-default-service';
import { LotListDataService } from 'src/app/modules/shared-components/lot-list-data-service/lot-list-data.service';
import {
  LotDataFragment,
  LotSource,
  LotTypeEnum,
} from '../../../graphql/service/graphql-auto-main-service';

import { AutoLotsListFilterService } from './services/auto-lots-list-filter.service';
import { LotWatchRequestTimeLimitService } from '../../../../services/lot-watch-request-time-limit.service';
import { AutoLotsListWatchRequestService } from './services/auto-lots-list-watch-request.service';
import { normalizeFrame } from 'src/app/utils/frame.util';

@Component({
  selector: 'app-auto-lots-list',
  templateUrl: './auto-lots-list.component.html',
  styleUrls: ['./auto-lots-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    AutoLotsListFilterService,
    AutoLotsListWatchRequestService,
    LotWatchRequestTimeLimitService,
  ],
})
export class ListComponent implements OnInit, OnDestroy {
  normalizeFrame = normalizeFrame;
  lotsListRef: WatchQueryRef<LotsListQuery, LotsListQueryVariables>;

  lots$: Observable<LotsListQuery['lots']['nodes']> = of([]);
  total$ = of(0);
  loading$ = of(true);

  lotListData$: Observable<Map<string, LotDataFragment>> = of(new Map());
  lotInspectedData$: Observable<Map<string, boolean>> = of(new Map());
  lotWatchRequestAvailableData$: Observable<Map<string, boolean>> = of(new Map());

  LotTypeEnum = LotTypeEnum;
  LotSource = LotSource;

  page = 1;
  pageSize = 20;

  private destroy$ = new Subject<void>();

  constructor(
    title: Title,
    private route: ActivatedRoute,
    private router: Router,
    private lotListDataService: LotListDataService,
    private autoLotsListFilterService: AutoLotsListFilterService,
    private autoLotsListWatchRequestService: AutoLotsListWatchRequestService,
  ) {
    title.setTitle('ECar JP Lots');
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.page = +params.page || 1;
      this.pageSize = +params.pageSize || this.pageSize;

      const variables = this.autoLotsListFilterService.buildVariables(
        params,
        this.page,
        this.pageSize,
      );

      this.fetchLots(variables);
    });
  }

  fetchLots(variables: LotsListQueryVariables): void {
    if (this.lotsListRef) {
      this.lotsListRef.refetch(variables, { triggerLoading: true });
    } else {
      this.lotsListRef = this.lotListDataService.watchLots(variables);
      this.loading$ = this.lotsListRef.loading.pipe(startWith(true), shareReplay(1));
      const lotsResult$ = this.lotsListRef.data.pipe(
        map((data) => data.lots),
        shareReplay(1),
      );
      this.lots$ = lotsResult$.pipe(map((lots) => lots.nodes));
      this.total$ = lotsResult$.pipe(map((lots) => lots.total));

      this.fetchLotsAdditionalData();
    }
  }

  fetchLotsAdditionalData(): void {
    this.lotListData$ = this.lots$.pipe(
      switchMap((lots) =>
        this.lotListDataService.fetchData(lots, LotTypeEnum.Auto, LotSource.Aleado).pipe(
          startWith(new Map<string, LotDataFragment>()),
          catchError(() => of(new Map<string, LotDataFragment>())),
        ),
      ),
      shareReplay(1),
    );

    this.lotInspectedData$ = this.lots$.pipe(
      switchMap((lots) =>
        this.lotListDataService.fetchInspectedData(lots, LotTypeEnum.Auto, LotSource.Aleado).pipe(
          startWith(new Map<string, boolean>()),
          catchError(() => of(new Map<string, boolean>())),
        ),
      ),
      shareReplay(1),
    );

    this.lotWatchRequestAvailableData$ = combineLatest([
      this.lots$,
      this.lotListData$,
      this.lotInspectedData$,
    ]).pipe(
      switchMap(([lots, lotListData, lotInspectedData]) =>
        this.autoLotsListWatchRequestService.getAvailableData(lots, lotListData, lotInspectedData),
      ),
      startWith(new Map<string, boolean>()),
      catchError(() => of(new Map<string, boolean>())),
      shareReplay(1),
    );
  }

  changePage(page: number): void {
    this.router.navigate(['auto'], {
      queryParams: {
        page,
      },
      queryParamsHandling: 'merge',
    });
  }

  changePageSize(pageSize: number): void {
    this.router.navigate(['auto'], {
      queryParams: {
        pageSize,
      },
      queryParamsHandling: 'merge',
    });
  }

  sortEvent(sort: { key: string; value: string }): void {
    this.router.navigate(['auto'], {
      queryParams: this.autoLotsListFilterService.buildSortQueryParams(sort),
      queryParamsHandling: 'merge',
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
